import { useWebSocket } from '@shared/hooks/useWebSocket';
import type { BatteryStatus, HeartbeatItem, HeartbeatSnapshot } from '@shared/lib/api/types';
import type { RobotPose } from '@shared/lib/api/navigation';
import { wsUrl, robotId } from '@shared/lib/config';
import { isKiosk } from '@shared/lib/constants';
import { getAuthToken } from '@/lib/authToken';
import { useRobotStore } from '@/stores/useRobotStore';

function findNavHeartbeat(heartbeats: HeartbeatItem[]): HeartbeatItem | undefined {
  return heartbeats.find((hb) => hb.heartbeat_id === 'navigation');
}

function findEstopActive(heartbeats: HeartbeatItem[]): boolean {
  return heartbeats.find((hb) => hb.heartbeat_id === 'arm_server')?.payload?.estop_active === true;
}

function extractBattery(heartbeats: HeartbeatItem[]): BatteryStatus | null {
  // Battery now flows as a heartbeat emitted by state_authority's BatteryPoller.
  // Treat dead/missing as "no battery info".
  const hb = heartbeats.find((h) => h.heartbeat_id === 'battery_status');
  if (!hb || hb.status === 'dead' || !hb.payload) return null;
  return hb.payload as BatteryStatus;
}

// Display-only labels — backend IDs are unchanged, so feed URLs / RPC calls
// keyed off `id` continue to work.
const CAMERA_DISPLAY_NAMES: Record<string, string> = {
  sentinel_left: 'Left Gripper',
  sentinel_right: 'Right Gripper',
  third_person: 'Center Camera',
};

function extractCameras(heartbeats: HeartbeatItem[]): Array<{ id: string; name: string }> {
  const cameraHb = heartbeats.find((hb) => hb.heartbeat_id === 'observation_generator_rpc');
  const available = cameraHb?.payload?.cameras_available as string[] | undefined;
  if (!Array.isArray(available)) return [];
  return available.map((id) => ({ id, name: CAMERA_DISPLAY_NAMES[id] ?? id.replace(/_/g, ' ') }));
}

function applyNavHeartbeats(
  heartbeats: HeartbeatItem[],
  setNavPose: (p: RobotPose | null) => void,
  setNavPath: (p: Array<[number, number]>) => void,
  setNavStatus: (s: string | null) => void,
  setBaseOwner: (owner: string | null) => void,
  setTeleopUser: (user: string | null) => void,
  setPositionKnown: (val: boolean | null) => void,
  setLocalizationDone: (val: boolean | null) => void,
) {
  const fbHb = heartbeats.find((hb) => hb.heartbeat_id === 'flow_base_service');
  if (fbHb && fbHb.status !== 'dead') {
    const fbp = fbHb.payload ?? {};
    setBaseOwner(typeof fbp.base_owner === 'string' ? fbp.base_owner : null);
    setTeleopUser(typeof fbp.teleop_user === 'string' ? fbp.teleop_user : null);
  } else {
    setBaseOwner(null);
    setTeleopUser(null);
  }

  const navHb = findNavHeartbeat(heartbeats);
  if (!navHb?.payload) return;
  const p = navHb.payload;
  if (p.map_pose != null) {
    const pose = p.map_pose as { x: number; y: number; theta?: number };
    setNavPose({ x: pose.x, y: pose.y, theta: pose.theta ?? 0 });
  } else {
    setNavPose(null);
  }
  if (Array.isArray(p.path)) {
    setNavPath(p.path as Array<[number, number]>);
  } else {
    setNavPath([]);
  }
  setNavStatus(typeof p.nav_status === 'string' ? p.nav_status : null);
  setPositionKnown(typeof p.position_known === 'boolean' ? p.position_known : null);
  setLocalizationDone(typeof p.localization_done === 'boolean' ? p.localization_done : null);
}


export function useRobotRealtime() {
  const setConnected = useRobotStore((s) => s.setConnected);
  const setRobotOnline = useRobotStore((s) => s.setRobotOnline);
  const setBattery = useRobotStore((s) => s.setBattery);
  const setNavPose = useRobotStore((s) => s.setNavPose);
  const setNavPath = useRobotStore((s) => s.setNavPath);
  const setNavStatus = useRobotStore((s) => s.setNavStatus);
  const setBaseOwner = useRobotStore((s) => s.setBaseOwner);
  const setTeleopUser = useRobotStore((s) => s.setTeleopUser);
  const setStateMachineState = useRobotStore((s) => s.setStateMachineState);
  const setRecoveryDone = useRobotStore((s) => s.setRecoveryDone);
  const setHealingInProgress = useRobotStore((s) => s.setHealingInProgress);
  const setEstopActive = useRobotStore((s) => s.setEstopActive);
  const setPositionKnown = useRobotStore((s) => s.setPositionKnown);
  const setLocalizationDone = useRobotStore((s) => s.setLocalizationDone);
  const setNavAvailable = useRobotStore((s) => s.setNavAvailable);
  const setCameras = useRobotStore((s) => s.setCameras);
  const setHeartbeats = useRobotStore((s) => s.setHeartbeats);
  const setWarnings = useRobotStore((s) => s.setWarnings);
  const setLastFullStatusAt = useRobotStore((s) => s.setLastFullStatusAt);

  useWebSocket({
    url: (() => {
      // Cloud users without a robot_id query param have nothing to connect
      // to — but the local kiosk explicitly *wants* the local /data/ws even
      // though robotId is null (we don't append ?robot_id= so apiPath() stays
      // in local mode). wsUrl() already handles the null-robotId case by
      // returning ws://<host>/data/ws.
      if (!robotId && !isKiosk) return null;
      const base = wsUrl();
      const token = getAuthToken();
      return token ? `${base}${base.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : base;
    })(),

    onOpen: () => setConnected(true),

    onClose: () => {
      setConnected(false);
      setRobotOnline(false);
      setBattery(null);
      setStateMachineState(null);
      setRecoveryDone(false);
      setHealingInProgress(false);
      setNavAvailable(false);
      setWarnings([]);
    },

    onMessage: (data) => {
      try {
        const msg = JSON.parse(data) as { type: string; [key: string]: unknown };

        if (msg.type === 'robot_offline') {
          setRobotOnline(false);
          setBattery(null);
          setNavPose(null);
          setNavPath([]);
          setStateMachineState(null);
          setRecoveryDone(false);
          setHealingInProgress(false);
          setNavAvailable(false);
          setCameras([]);
          setHeartbeats([]);
          setWarnings([]);
          return;
        }

        if (msg.type === 'full_status') {
          setRobotOnline(true);
          setLastFullStatusAt(Date.now());

          // heartbeat data is nested under heartbeat_status (a HeartbeatSnapshot)
          if (msg.heartbeat_status != null) {
            const hs = msg.heartbeat_status as HeartbeatSnapshot;
            const hbs = hs.heartbeats ?? [];
            setHeartbeats(hbs);
            applyNavHeartbeats(hbs, setNavPose, setNavPath, setNavStatus, setBaseOwner, setTeleopUser, setPositionKnown, setLocalizationDone);
            setNavAvailable(findNavHeartbeat(hbs) !== undefined);
            setEstopActive(findEstopActive(hbs));
            setCameras(extractCameras(hbs));
            setBattery(extractBattery(hbs));
            if (typeof hs.state_machine?.state === 'string') {
              setStateMachineState(hs.state_machine.state);
            }
            setRecoveryDone(hs.state_machine?.recovery_done ?? false);
            setHealingInProgress(hs.state_machine?.healing_in_progress ?? false);
            setWarnings(hs.state_machine?.warnings ?? []);
          }
          return;
        }

        // Standalone heartbeat push — payload is the HeartbeatSnapshot itself
        if (msg.type === 'heartbeat_status') {
          const hs = msg as unknown as HeartbeatSnapshot;
          const hbs = hs.heartbeats ?? [];
          setHeartbeats(hbs);
          applyNavHeartbeats(hbs, setNavPose, setNavPath, setNavStatus, setBaseOwner, setTeleopUser, setPositionKnown, setLocalizationDone);
          setNavAvailable(findNavHeartbeat(hbs) !== undefined);
          const cameras = extractCameras(hbs);
          if (cameras.length > 0) setCameras(cameras);
          setEstopActive(findEstopActive(hbs));
          setBattery(extractBattery(hbs));
          setRecoveryDone(hs.state_machine?.recovery_done ?? false);
          setHealingInProgress(hs.state_machine?.healing_in_progress ?? false);
          setWarnings(hs.state_machine?.warnings ?? []);
          return;
        }
      } catch {
        // malformed message — ignore
      }
    },
  });
}
